import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus, EmptyStatus} from 'platform/components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useGetSaleVehicleFinancingQuery,
  useGetSaleVehicleLeasingQuery,
  useGetSaleVehicleQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {usePermissions, useTenant} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {FinancingAndLeasingEditor} from './FinancingAndLeasingEditor';
import {FinancingSection} from './types/FinancingSection';
import {resolveAccessibleSection} from './utils/resolveAccessibleSection';

export function VehicleFinancingAndLeasing() {
  const {id: vehicleId} = useRequiredParams();
  const {tenantCurrency} = useTenant();
  const [section, setSection] = useState<FinancingSection>('FINANCING');
  const {
    data: saleVehicle,
    isLoading: isSaleVehicleLoading,
    isError: isSaleVehicleError,
  } = useGetSaleVehicleQuery({vehicleId});
  const [
    canReadFinancing,
    canCreateFinancing,
    canUpdateFinancing,
    canDeleteFinancing,
    canReadLeasing,
    canCreateLeasing,
    canUpdateLeasing,
    canDeleteLeasing,
    canReadTemplates,
  ] = usePermissions({
    permissionKeys: [
      'saleVehicleFinancingRead',
      'saleVehicleFinancingCreate',
      'saleVehicleFinancingUpdate',
      'saleVehicleFinancingDelete',
      'saleVehicleLeasingRead',
      'saleVehicleLeasingCreate',
      'saleVehicleLeasingUpdate',
      'saleVehicleLeasingDelete',
      'readVehicleFinancingTemplate',
    ],
  });
  const saleVehicleId = saleVehicle?.id ?? '';
  const currency = saleVehicle?.currency ?? tenantCurrency;
  const accessibleSection = resolveAccessibleSection(section, canReadFinancing, canReadLeasing);
  // Both overviews feed the "configured" tick on their own tab, so both are fetched regardless of
  // which tab is open. Gating them on the active section made a skipped hook return `undefined`,
  // which silently dropped the inactive tab's badge. Only the read permissions may skip a request.
  const {data: financingOverview} = useGetSaleVehicleFinancingQuery(
    canReadFinancing && isNotNil(saleVehicle?.id) ? {saleVehicleId: saleVehicle.id} : skipToken
  );
  const {data: leasingOverview} = useGetSaleVehicleLeasingQuery(
    canReadLeasing && isNotNil(saleVehicle?.id) ? {saleVehicleId: saleVehicle.id} : skipToken
  );

  if (!canReadFinancing && !canReadLeasing) {
    return (
      <Card>
        <EmptyStatus
          headline={i18n.t('page.vehicle.financingAndLeasing.noReadPermission')}
          data-testid="vehicle-financing-and-leasing-no-read-permission"
        />
      </Card>
    );
  }

  return (
    <DataStatus
      isLoading={isSaleVehicleLoading}
      isError={isSaleVehicleError}
      isEmpty={isNil(saleVehicle)}
      emptyMessage={i18n.t('page.vehicle.financingAndLeasing.noSaleVehicle')}
    >
      <FinancingAndLeasingEditor
        key={saleVehicleId}
        saleVehicleId={saleVehicleId}
        currency={currency}
        vehiclePrice={saleVehicle?.sellingPrice.withVat ?? null}
        activeSection={accessibleSection}
        onSectionChange={setSection}
        canReadFinancing={canReadFinancing}
        canCreateFinancing={canCreateFinancing}
        canUpdateFinancing={canUpdateFinancing}
        canDeleteFinancing={canDeleteFinancing}
        canReadTemplates={canReadTemplates}
        canReadLeasing={canReadLeasing}
        canCreateLeasing={canCreateLeasing}
        canUpdateLeasing={canUpdateLeasing}
        canDeleteLeasing={canDeleteLeasing}
        hasFinancing={(financingOverview?.configurations.length ?? 0) > 0}
        hasLeasing={leasingOverview?.state === 'CURRENT'}
      />
    </DataStatus>
  );
}
